import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { MessageSquare, CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [errorMsg, setErrorMsg] = useState('');
  const [requiresCheckout, setRequiresCheckout] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Token de verificacion no encontrado');
      return;
    }
    const verify = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/verify-email?token=${token}`, { withCredentials: true });
        setStatus('success');
        setRequiresCheckout(data.requires_checkout);
      } catch (e) {
        setStatus('error');
        setErrorMsg(e.response?.data?.detail || 'Error al verificar el email');
      }
    };
    verify();
  }, [token]);

  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        navigate(requiresCheckout ? '/plans' : '/dashboard', { replace: true });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, requiresCheckout, navigate]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <MessageSquare className="w-7 h-7 text-indigo-600" />
          <span className="text-xl font-bold text-gray-950">ACBotHub</span>
        </div>

        {status === 'verifying' && (
          <div data-testid="verify-loading">
            <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-950 mb-2">Verificando tu email...</h2>
            <p className="text-gray-500">Espera un momento</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="verify-success">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-950 mb-2">Email verificado</h2>
            <p className="text-gray-500 mb-6">
              {requiresCheckout
                ? 'Tu cuenta esta activa. Redirigiendo a los planes...'
                : 'Tu cuenta esta activa. Redirigiendo al dashboard...'}
            </p>
            <Button
              onClick={() => navigate(requiresCheckout ? '/plans' : '/dashboard', { replace: true })}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-6 py-2.5"
              data-testid="verify-continue-btn"
            >
              Continuar
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="verify-error">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-950 mb-2">Error de verificacion</h2>
            <p className="text-red-600 mb-6">{errorMsg}</p>
            <Link to="/register">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-6 py-2.5" data-testid="verify-retry-btn">
                Intentar de nuevo
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
